import { createContext, useContext, useState, useCallback, FC } from "react";
import { AuthProvider, useAuthContext } from "./AuthProvider";
import AuthButton from "./AuthButton";
import { ChatBot } from "./ChatBot";
import { FirstRunExperience } from "./FirstRunExperience";
import Header from "./Header";
import { Plus } from "lucide-react";

// Context for sharing new conversation function
interface ChatContextType {
	startNewConversation: () => void;
	setStartNewConversation: (fn: () => void) => void;
	loadConversation: (id: string) => void;
	setLoadConversation: (fn: (id: string) => void) => void;
	conversationId: string | null;
	setConversationId: (id: string | null) => void;
	hasMessages: boolean;
	setHasMessages: (has: boolean) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

const useChatContextValue: () => ChatContextType = () => {
	const [startNewConversation, setStartNewConversation] = useState<() => void>(() => { });
	const [loadConversation, setLoadConversation] = useState<(id: string) => void>(() => () => { });
	const [conversationId, setConversationId] = useState<string | null>(null);
	const [hasMessages, setHasMessages] = useState(false);

	const value: ChatContextType = {
		startNewConversation,
		setStartNewConversation,
		loadConversation,
		setLoadConversation,
		conversationId,
		setConversationId,
		hasMessages,
		setHasMessages,
	};

	return value;
};

export const useChatContext = () => {
	const context = useContext(ChatContext);
	if (!context) {
		throw new Error("useChatContext must be used within ChatContextProvider");
	}
	return context;
};

/**
 * Inner component that has access to auth context
 */
const AppContent: FC = () => {
	const { user, signIn } = useAuthContext();
	const chatContextValue = useChatContextValue();

	// Show first-run experience when user is not authenticated
	if (!user) {
		return <FirstRunExperience onGetStarted={signIn} />;
	}

	// Show main interface (ChatBot) for authenticated users
	return (
		<ChatContext.Provider value={chatContextValue}>
			<div className="h-screen relative flex flex-col">
				<Header />

				<div className="w-full h-full pt-10">
					<ChatBot />
				</div>
			</div>
		</ChatContext.Provider>
	);
};

const App: FC = () => {
	return (
		<AuthProvider>
			<AppContent />
		</AuthProvider>
	);
};

export default App;
